import { createFileRoute, Link } from "@tanstack/react-router";
import { Leaf, Plus } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyDashboard } from "@/components/EmptyDashboard";
import { OfflineActionGuard } from "@/components/OfflineActionGuard";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ResponsiveFormDialog } from "@/components/ui/responsive-form-dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { DashboardSkeleton } from "@/components/ui/skeleton";
import { useEstablishment } from "@/hooks/useEstablishment";
import { useFarmScope } from "@/hooks/useFarmScope";
import { useOfflineGuard } from "@/hooks/useOfflineGuard";
import { AllPaddocksList } from "./-components/AllPaddocksList";

type GrassSearch = { farm?: string };

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/grass-production/",
)({
	// Paddock list degrades to the prepared offline dataset.
	staticData: { offline: "ready" },
	validateSearch: (search: Record<string, unknown>): GrassSearch => ({
		// The org-level farm scope param; inherited from the org route.
		farm: typeof search.farm === "string" ? search.farm : undefined,
	}),
	component: GrassProductionIndex,
});

/**
 * Grass Production hub — one simple page: the searchable paddock list. Measurements, operations and soil analysis are
 * managed inside each paddock's detail page, not here.
 */
function GrassProductionIndex() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const { establishments, isLoading } = useEstablishment();
	const scope = useFarmScope();
	const { blocked } = useOfflineGuard();
	const navigate = Route.useNavigate();
	const farmPickerId = useId();
	// Holds the chosen target farm while All Farms is active (a paddock always
	// belongs to one farm, so a create needs a concrete establishment).
	const [isFarmPickerOpen, setIsFarmPickerOpen] = useState(false);
	const [pickerFarmId, setPickerFarmId] = useState("");

	const goToNewPaddock = (farmId: string) =>
		navigate({
			to: "/app/org/$orgId/grass-production/farms/$farmId/paddocks/new",
			params: { orgId, farmId },
		});

	const handleAddPaddock = () => {
		if (scope.kind === "single") {
			goToNewPaddock(scope.establishmentId);
			return;
		}
		setPickerFarmId("");
		setIsFarmPickerOpen(true);
	};

	const handleConfirmFarm = () => {
		if (!pickerFarmId) return;
		setIsFarmPickerOpen(false);
		goToNewPaddock(pickerFarmId);
	};

	if (isLoading) {
		return <DashboardSkeleton />;
	}

	// One definition, two placements: the desktop toolbar on the right of the
	// title row, and the phone's eyebrow row, where `compact` trades the verb
	// away — "+ Piquete" fits beside "Pastagem", "Criar Piquete" crowds it. The
	// cultivars link is icon-only there for the same reason.
	const cultivarsAction = (compact: boolean) => (
		<OfflineActionGuard blocked={blocked}>
			<Link to="/app/org/$orgId/grass-production/cultivars" params={{ orgId }}>
				<Button
					variant="outline"
					size={compact ? "sm" : "default"}
					className={compact ? "h-7 w-7 p-0" : undefined}
					aria-disabled={blocked}
					aria-label={t("grass.cultivar.list.title")}
				>
					<Leaf className={compact ? "h-3.5 w-3.5" : "mr-2 h-4 w-4"} />
					{!compact && t("grass.cultivar.list.title")}
				</Button>
			</Link>
		</OfflineActionGuard>
	);
	const actions = (compact: boolean) => (
		<>
			{cultivarsAction(compact)}
			<OfflineActionGuard blocked={blocked}>
				<Button
					size={compact ? "sm" : "default"}
					className={compact ? "h-7 gap-1 px-2 text-[12px]" : undefined}
					onClick={handleAddPaddock}
					aria-disabled={blocked}
				>
					<Plus className={compact ? "h-3.5 w-3.5" : "mr-2 h-4 w-4"} />
					{compact
						? t("grass.paddock.actions.createShort")
						: t("grass.paddock.actions.create")}
				</Button>
			</OfflineActionGuard>
		</>
	);

	// No farms - show empty state with guidance.
	if (establishments.length === 0) {
		return (
			<div className="flex flex-col gap-6">
				<PageHeader
					title={t("grass.nav.title")}
					actions={cultivarsAction(false)}
				/>
				<EmptyDashboard
					title={t("grass.farm.noFarms")}
					description={t("grass.farm.noFarmsDescription")}
					icon={Leaf}
					action={{
						label: t("common.establishments"),
						to: "/app/org/$orgId/settings/establishments",
						params: { orgId },
					}}
				/>
			</div>
		);
	}

	return (
		// Tighter stack on a phone, matching the herd list: the eyebrow is a
		// header for the list below it.
		<div className="flex flex-col gap-3 md:gap-6">
			{/* Desktop keeps the titled toolbar. On a phone the section name rides
			    an eyebrow row (the bottom nav already identifies it, and the top
			    farm selector the scope) with the actions compacted beside it —
			    same shape as the livestock hub, so the two sections read as one. */}
			<PageHeader
				title={t("grass.nav.title")}
				hideOnMobile
				actions={
					<div className="flex flex-wrap items-center gap-2">
						{actions(false)}
					</div>
				}
			/>
			<div className="flex min-h-8 items-center justify-between gap-2 md:hidden">
				<span className="font-bold text-[12px] text-base-content/70 uppercase tracking-[0.14em]">
					{t("common.grassProduction")}
				</span>
				<div className="flex shrink-0 items-center gap-2">{actions(true)}</div>
			</div>
			<AllPaddocksList orgId={orgId} scope={scope} />

			{/* All Farms create needs a concrete target farm before the form. */}
			<ResponsiveFormDialog
				open={isFarmPickerOpen}
				onOpenChange={setIsFarmPickerOpen}
				title={t("grass.paddock.actions.create")}
				footer={
					<>
						<Button
							variant="outline"
							onClick={() => setIsFarmPickerOpen(false)}
						>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleConfirmFarm} disabled={!pickerFarmId}>
							{t("common.continue")}
						</Button>
					</>
				}
			>
				<div className="grid gap-2">
					<Label htmlFor={farmPickerId}>{t("grass.farm.selectFarm")}</Label>
					<Select value={pickerFarmId} onValueChange={setPickerFarmId}>
						<SelectTrigger id={farmPickerId} className="w-full">
							<SelectValue placeholder={t("common.selectEstablishment")} />
						</SelectTrigger>
						<SelectContent>
							{establishments.map((farm) => (
								<SelectItem key={farm.id} value={farm.id}>
									{farm.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</ResponsiveFormDialog>
		</div>
	);
}
